import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap, forkJoin } from 'rxjs';
import { AppConfigService } from '@core/app-config/app-config.service';
import { ActivatedRouteSnapshot } from '@angular/router';
import { AvailableTranslationsService } from '@core/translations/available-translations.service';
import { TranslateService } from '@ngx-translate/core';
import { InitialConfigService } from '@core/app-config/initial-config.service';
import { NavigationService } from '@core/navigation/navigation.service';
import { catchError, map, tap } from 'rxjs/operators';
import { LoggerService } from '@core/logger/logger.service';
import { IInitialConfig } from '@core/venue-config/initial-config.model';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { WhenAttendingService } from '@app/when-attending/when-attending.service';
import { DateFormatService } from '@core/date/date-format.service';
import { IVenueConfig } from '@core/venue-config/models/venue-config.model';
import { AuthService } from '@core/auth/auth.service';
import { AccountService } from '@core/account/account.service';
import { IBasicCustomer } from '@core/customer/basic-customer.model';
import { HttpErrorResponse } from '@angular/common/http';
import { AbTestingService } from '../analytics/ab-testing/ab-testing.service';
import { UpgradeService } from '../upgrade/upgrade.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { OrderService } from '@core/order/order.service';
import { StorageService } from '@core/storage/storage.service';
import { CPickUpTableNumber } from '@dialog/dialog-table-number/dialog-table-number.component.model';
import { GuestCountService } from '@core/guest-count/guest-count.service';

@Injectable({
  providedIn: 'root'
})
export class BootstrapService {
  private isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isAppInitialized = false;

  constructor(
    private logger: LoggerService,
    private appConfigService: AppConfigService,
    private availableTranslationsService: AvailableTranslationsService,
    private translateService: TranslateService,
    private initialConfigService: InitialConfigService,
    private navigationService: NavigationService,
    private venueConfigService: VenueConfigService,
    private whenAttendingService: WhenAttendingService,
    private dateFormatService: DateFormatService,
    private authService: AuthService,
    private accountService: AccountService,
    private upgradeService: UpgradeService,
    private abTestingService: AbTestingService,
    private analyticsService: AnalyticsService,
    private orderService: OrderService,
    private storageService: StorageService,
    private guestCountService: GuestCountService
  ) {}

  getIsAppInitialized(): boolean {
    return this.isAppInitialized;
  }

  getIsLoading$(): Observable<boolean> {
    return this.isLoading$.asObservable();
  }

  setIsLoading(value: boolean): void {
    this.isLoading$.next(value);
  }

  /**
   * Build initial config
   * Load products, settings, translations in parallel
   * Set auth state and load account
   */
  bootstrapApplication(route: ActivatedRouteSnapshot): Observable<boolean> {
    const timeStart = Date.now();
    this.setIsLoading(true);
    const discountCode = route.queryParams.code;

    const baseUrl = this.appConfigService.getEnvValue<string>('rollerApiBaseUrl');

    return this.initialConfigService.loadInitialConfig(baseUrl, route).pipe(
      tap((config) => {
        this.initialConfigService.setInitialConfig(config);
        this.setExperimentalStatus(config);
      }),
      switchMap((config) =>
        forkJoin([this.boostrapTranslations(route), this.venueConfigService.loadVenueConfig(config, discountCode)])
      ),
      switchMap(([, venueConfig]) => this.bootstrapVenueConfig(venueConfig)),
      switchMap((venueConfig) => this.getInitialAuthState(venueConfig)),
      switchMap(() => this.authService.getIsLoggedIn()),
      switchMap((isLoggedIn) => this.getGuestAccount(isLoggedIn)),
      map((account: IBasicCustomer | undefined) => {
        this.setAccountDetails(account);
        this.setStorageOrder();
        this.setInitialTableNumber();
        this.setExternalBookingId();
        this.setGuestCount();
        this.isAppInitialized = true;
        this.setIsLoading(false);
        console.log(`App loaded in ${Date.now() - timeStart}ms`);

        return this.isAppInitialized;
      })
    );
  }

  /** Loads available translations gracefully and configures language settings for the app */
  private boostrapTranslations(routeSnapshot: ActivatedRouteSnapshot): Observable<IInitialConfig> {
    return this.availableTranslationsService.getAvailableTranslations().pipe(
      catchError((e: HttpErrorResponse) => {
        this.logger.warn('Error loading available translations', e?.error);
        return of(undefined);
      }),
      map((translations) => {
        this.translateService.addLangs(translations?.availableLanguageCodes || []);
        const lang = this.availableTranslationsService.getLanguageCode(routeSnapshot);
        this.setLanguageDetails(lang);

        const initConfig: IInitialConfig = { ...this.initialConfigService.getInitialConfig(), lang };
        this.initialConfigService.setInitialConfig(initConfig);
        this.navigationService.setBaseVenueCheckoutUrlPortion(initConfig);

        return initConfig;
      })
    );
  }

  private async bootstrapVenueConfig(venueConfig: IVenueConfig): Promise<IVenueConfig> {
    this.venueConfigService.setVenueConfig(venueConfig);
    await this.dateFormatService.setVenueLocale(
      venueConfig.venueSettings.dateTimeLocale,
      this.initialConfigService.getInitialConfig().lang
    );
    this.whenAttendingService.setInitialDate(venueConfig);

    return venueConfig;
  }

  private getGuestAccount(isLoggedIn: boolean): Observable<undefined> | Observable<IBasicCustomer> {
    const config = this.venueConfigService.getVenueConfig();
    if (isLoggedIn && config.venueSettings.isGuestAccountsEnabled) {
      return this.accountService.fetchGuestAccount();
    }

    return of(undefined);
  }

  private setLanguageDetails(lang: string): void {
    this.translateService.setDefaultLang(this.availableTranslationsService.getDefaultLanguageCode());
    this.translateService.use(lang);
  }

  /** If guest accounts are not enabled, or they are not logged in, simply load any account details from storage */
  private setAccountDetails(account: IBasicCustomer | undefined): void {
    if (account) {
      this.accountService.mergeAndSetAccounts(account);
      return;
    }

    const storageAccount = this.accountService.getStorageUserAccount();
    if (storageAccount) {
      this.accountService.setUserAccount(storageAccount);
    }
  }

  private setStorageOrder() {
    const storedOrder = this.orderService.loadStorageOrder();
    if (!storedOrder) {
      this.storageService.clearTicketUserInfo();
    }
  }

  private setInitialTableNumber() {
    if (!this.venueConfigService.isFoodAndBeverageCheckout()) {
      return;
    }

    const config = this.venueConfigService.getVenueConfig();
    if (config.productsPage.isPickupOnly) {
      this.orderService.setTableNumber(CPickUpTableNumber);
      return;
    }

    const tableNumberFromUrl = this.navigationService.getTableNumberFromUrl();
    if (tableNumberFromUrl) {
      this.orderService.setTableNumber(tableNumberFromUrl);
    }
  }

  private setExternalBookingId() {
    const externalId = this.navigationService.getExternalIdFromUrl();
    if (externalId) {
      this.orderService.setExternalId(externalId);
    }
  }

  private setGuestCount() {
    const guestCount = this.guestCountService.getStoredGuestCount();
    if (guestCount) {
      this.guestCountService.setGuestCount(guestCount);
    }
  }

  /**
   * Set the initial logged in state based on checkout setting and storage auth. Immediately attempt sign out if guest  accounts are disabled while stored auth is still valid.
   * This method is graceful, and allows bootstrap to continue regardless of result
   */
  private getInitialAuthState(venueConfig: IVenueConfig): Observable<undefined> {
    const isStoredAuthValid = this.authService.isStoredAuthValid();
    const isLoggedInValid = !!venueConfig.venueSettings.isGuestAccountsEnabled && isStoredAuthValid;

    if (isStoredAuthValid && !isLoggedInValid) {
      return this.authService.signOut();
    }

    this.authService.setIsLoggedIn(isLoggedInValid);
    return of(undefined);
  }

  /**
   * Loads any hard coded venue upgrade data and uses the ab testing service to determine if the guest
   * is in control (A) or experiment (B). Future use of this will expand the number of experiments
   * !TEMP Experiment upgrade offer load - if built will be part of the checkout config
   */
  private async setExperimentalStatus(config: IInitialConfig) {
    const checkUpgradeVenueSlugsContains = ['qarollerpayment', 'dreamlanddev'];
    let experiment = '';
    let isInExperimentGroupB = false;
    if (checkUpgradeVenueSlugsContains.some((slug) => config.siteKey.includes(slug))) {
      const upgradeList = await this.upgradeService.fetchUpgradeList(config.siteKey, config.checkoutSlug);
      if (upgradeList?.length) {
        this.abTestingService.initAbTesting();
        isInExperimentGroupB = this.abTestingService.isShownExperiment();
        experiment = 'upgrade';
      }
    }

    this.analyticsService.setExperimentalStatus(experiment, isInExperimentGroupB);
  }
}
